import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment,
  Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useMediaQuery, useTheme,
} from "@material-ui/core";
import { ArrowBack, AssignmentTurnedInOutlined, Clear, ConfirmationNumberOutlined, DeleteOutline, GetApp, InfoOutlined, Refresh, Search } from "@material-ui/icons";
import { Alert, Pagination, Skeleton } from "@material-ui/lab";
import PageContainer from "../../../@jumbo/components/PageComponents/layouts/PageContainer";
import IntlMessages from "../../../@jumbo/utils/IntlMessages";
import useStyles from "@jumbo/Dashboards/FormBuilder/recordListStyles";
import axios from "../../../services/auth/jwt/config";
import { errorText } from "../FormTickets/utils";
import FormInputListItem, { participantName, personnelCode } from "./FormInputListItem";

const breadcrumbs = [
  { label: <IntlMessages id={"sidebar.main"} />, link: "/" },
  { label: <IntlMessages id={"pages.forms"} />, link: "/forms" },
  { label: <IntlMessages id={"pages.formInputs"} />, isActive: true },
];

function FormInputManager({ id, initialName }) {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState(null);
  const [formLoading, setFormLoading] = useState(true);
  const [formError, setFormError] = useState("");
  const [result, setResult] = useState({ docs: [], totalDocs: 0, totalPages: 0 });
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [menu, setMenu] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const loading = formLoading || recordsLoading;
  const privacyMode = form?._id !== id || Boolean(form?.privacyMode);

  useEffect(() => {
    let active = true;
    setFormLoading(true);
    setFormError("");
    axios.get(`/form/get/${id}`)
      .then(({ data }) => { if (active) setForm(data); })
      .catch(err => { if (active) { setForm(null); setFormError(errorText(err)); } })
      .finally(() => { if (active) setFormLoading(false); });
    return () => { active = false; };
  }, [id, revision]);

  useEffect(() => {
    let active = true;
    setRecordsLoading(true);
    setError("");
    axios.get(`/formfilled/getall/${id}`, { params: { page, limit: 10, searchString: search } })
      .then(({ data }) => {
        if (!active) return;
        setResult(data);
        if (page > Math.max(1, data.totalPages)) setPage(Math.max(1, data.totalPages));
      })
      .catch(err => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setRecordsLoading(false); });
    return () => { active = false; };
  }, [id, page, search, revision]);

  const remove = async () => {
    setBusy(true);
    setDeleteError("");
    try {
      await axios.delete(`/formfilled/remove/${deleting._id}`);
      setDeleting(null);
      setSuccess("ورودی با موفقیت حذف شد");
      setRevision(value => value + 1);
    } catch (err) { setDeleteError(errorText(err)); }
    finally { setBusy(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    let url;
    let link;
    try {
      const response = await axios.get(`/formfilled/get/file/${id}`, {
        params: { searchString: search },
        responseType: "blob",
      });
      const disposition = response.headers?.["content-disposition"] || "";
      const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const regular = /filename="([^"]+)"/i.exec(disposition);
      const filename = utf8 ? decodeURIComponent(utf8[1]) : regular?.[1] || "form-responses.xlsx";
      url = window.URL.createObjectURL(response.data instanceof Blob ? response.data : new Blob([response.data]));
      link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    } catch (err) { setDownloadError(errorText(err)); }
    finally {
      if (link?.parentNode) link.parentNode.removeChild(link);
      if (url) window.URL.revokeObjectURL(url);
      setDownloading(false);
    }
  };
  const firstRecord = result.docs.length ? (page - 1) * 10 + 1 : 0;
  const lastRecord = (page - 1) * 10 + result.docs.length;

  return <PageContainer heading={<IntlMessages id="pages.formInputs" />} breadcrumbs={breadcrumbs}>
    <Box className={classes.hero}>
      <Box className={classes.heroIdentity}>
        <Box className={classes.heroIcon}><AssignmentTurnedInOutlined /></Box>
        <Box><Typography className={classes.eyebrow}>مرور پاسخ‌های ثبت‌شده و دریافت گزارش فرم</Typography>
          <Typography component="h2" className={classes.heroTitle}>{form?.title || initialName || "ورودی‌های فرم"}</Typography></Box>
      </Box>
      <Box className={classes.heroActions}>
        <Button component={Link} to="/forms" startIcon={<ArrowBack />} className={classes.button}>بازگشت به فرم‌ها</Button>
        {form?.type === "Exam" && <Button component={Link} to={`/formtickets/${id}`} color="primary" variant="outlined" startIcon={<ConfirmationNumberOutlined />} className={classes.button}>مدیریت بلیط‌ها</Button>}
        <Button onClick={handleDownload} disabled={loading || busy || downloading} color="primary" variant="contained" startIcon={<GetApp />} className={classes.button}>{downloading ? "در حال دریافت گزارش…" : "فایل گزارش"}</Button>
        <Button onClick={() => setRevision(value => value + 1)} disabled={loading || busy} startIcon={<Refresh />} className={classes.button}>بارگذاری مجدد</Button>
      </Box>
    </Box>
    <Paper elevation={0} className={classes.panel}>
      <Box className={classes.panelHeader}>
        <Typography component="h3" className={classes.panelTitle}>ورودی‌های ثبت‌شده</Typography>
        {!loading && !error && <Box component="span" className={classes.count} px={1.5} py={0.5} borderRadius={8} fontSize={12}>{result.totalDocs.toLocaleString("fa-IR")} {search ? "نتیجه" : "ورودی"}</Box>}
      </Box>
      <Box component="form" className={classes.filters} onSubmit={event => { event.preventDefault(); setPage(1); setSearch(searchDraft.trim()); }}>
        <TextField id="form-input-search" label="جستجوی ورودی‌ها" placeholder="نام، کد پرسنلی، کد ملی یا پاسخ فرم" variant="outlined" size="small" value={searchDraft}
          onChange={event => setSearchDraft(event.target.value)} inputProps={{ maxLength: 200 }} className={classes.search}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search className={classes.searchIcon} /></InputAdornment> }} />
        <Button type="submit" color="primary" variant="contained" disabled={loading || busy} startIcon={<Search />} className={classes.button}>جستجو</Button>
        {search && <Button onClick={() => { setSearchDraft(""); setSearch(""); setPage(1); }} disabled={loading || busy} startIcon={<Clear />} className={classes.button}>پاک کردن جستجو</Button>}
      </Box>
      {formError && <Alert severity="warning" className={classes.alert}>اطلاعات فرم دریافت نشد؛ اطلاعات شخصی پنهان شده است. {formError}</Alert>}
      {downloadError && <Alert severity="error" className={classes.alert} onClose={() => setDownloadError("")}>{downloadError}</Alert>}
      {success && <Alert severity="success" className={classes.alert} onClose={() => setSuccess("")}>{success}</Alert>}
      {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
      {loading ? <Box className={classes.loading}>
        <Typography role="status" color="textSecondary">در حال دریافت ورودی‌ها…</Typography>
        {[1, 2, 3].map(value => <Skeleton key={value} variant="rect" height={64} aria-hidden="true" />)}
      </Box> : !error && <>
        {!result.docs.length ? <Box className={classes.empty}><Search /><Typography className={classes.emptyTitle}>ورودی‌ای یافت نشد</Typography>
          <Typography variant="body2" color="textSecondary">{search ? "عبارت دیگری را جستجو کنید یا جستجو را پاک کنید." : "پس از ثبت پاسخ کاربران، ورودی‌ها در این بخش نمایش داده می‌شوند."}</Typography>
        </Box> : mobile ? <Box className={classes.mobileList}>
          {result.docs.map(record => <FormInputListItem key={record._id} record={record} form={form} privacyMode={privacyMode} mobile busy={busy} onMenu={(event, target) => setMenu({ anchor: event.currentTarget, record: target })} />)}
        </Box> : <TableContainer><Table aria-label="ورودی‌های فرم" className={classes.table}>
          <TableHead><TableRow>{["شرکت‌کننده", "اطلاعات تماس", form?.type === "Exam" ? "نتیجه آزمون" : "وضعیت ثبت‌نام", "تاریخ ثبت", "عملیات"].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{result.docs.map(record => <FormInputListItem key={record._id} record={record} form={form} privacyMode={privacyMode} busy={busy} onMenu={(event, target) => setMenu({ anchor: event.currentTarget, record: target })} />)}</TableBody>
        </Table></TableContainer>}
        {result.totalDocs > 0 && <Box className={classes.footer}>
          <Typography variant="caption" color="textSecondary">نمایش {firstRecord.toLocaleString("fa-IR")}–{lastRecord.toLocaleString("fa-IR")} از {result.totalDocs.toLocaleString("fa-IR")} ورودی</Typography>
          {result.totalPages > 1 && <Pagination count={result.totalPages} page={page} color="primary" shape="rounded" disabled={busy} onChange={(_, value) => setPage(value)} />}
        </Box>}
      </>}
    </Paper>
    <Box className={classes.help}><InfoOutlined /><Typography>برای مشاهده پاسخ‌ها و جزئیات ثبت، «نمایش پاسخ» را انتخاب کنید. فایل گزارش بر اساس جستجوی فعلی دریافت می‌شود.</Typography></Box>
    <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} classes={{ paper: classes.menuPaper }}
      getContentAnchorEl={null} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
      <MenuItem className={`${classes.menuItem} ${classes.deleteAction}`} onClick={() => { setDeleting(menu.record); setDeleteError(""); setMenu(null); }}><DeleteOutline />حذف ورودی</MenuItem>
    </Menu>
    <Dialog open={Boolean(deleting)} onClose={() => { if (!busy) setDeleting(null); }} fullWidth maxWidth="sm" aria-labelledby="form-input-delete-title" PaperProps={{ className: classes.dialogPaper }}>
      {deleting && <>
        <DialogTitle id="form-input-delete-title" className={classes.dialogTitle}>حذف ورودی فرم</DialogTitle>
        <DialogContent>
          <Box className={classes.dialogParticipant}><Avatar className={classes.avatar}>{participantName(deleting, privacyMode).trim().charAt(0)}</Avatar>
            <Box><Typography className={classes.participantName}>{participantName(deleting, privacyMode)}</Typography><Typography className={classes.secondary}>کد پرسنلی: {personnelCode(deleting, privacyMode)}</Typography></Box>
          </Box>
          <Alert severity="warning">با حذف این ورودی، پاسخ‌های ثبت‌شده آن حذف می‌شوند. {form?.type === "Exam" && "بلیط آزمون مرتبط نیز حذف می‌شود."} آیا اطمینان دارید؟</Alert>
          {deleteError && <Box mt={2}><Alert severity="error">{deleteError}</Alert></Box>}
        </DialogContent>
        <DialogActions className={classes.dialogActions}><Button disabled={busy} onClick={() => setDeleting(null)} className={classes.button}>انصراف</Button>
          <Button disabled={busy} color="secondary" variant="contained" onClick={remove} className={classes.button}>{busy ? "در حال حذف…" : "تایید حذف"}</Button>
        </DialogActions>
      </>}
    </Dialog>
  </PageContainer>;
}

const FormInputs = () => {
  const { id } = useParams();
  const location = useLocation();
  const name = new URLSearchParams(location.search).get("name");
  return <FormInputManager key={id} id={id} initialName={name} />;
};
export default FormInputs;
